import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { TesterAccessGrantView } from '@hospasense/contracts';
import { ApiError, apiGet, apiSend } from '../lib/api.js';
import { formatDateTime, relativeTime } from '../lib/format.js';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  ErrorState,
  Input,
  LoadingState,
  useToast,
} from '../components/ui/index.js';

/**
 * "Give tester access" — for SenseSys evaluation organisations only.
 *
 * Type a colleague's @sensesys.org address, press the button. The hospital
 * system sets one password on every role login, signs in as each of them to
 * prove it, and only then emails the colleague. This card shows exactly what
 * the hospital system reported back: "35 of 35 logins working · emailed", or
 * the reason it did not. The password itself never comes to Control.
 *
 * While a grant is in flight the list refreshes on its own, because the
 * hospital system picks commands up every few minutes, not instantly.
 */
export function TesterAccessCard({ orgId }: { orgId: string }) {
  const toast = useToast();
  const qc = useQueryClient();
  const [email, setEmail] = useState('');
  const key = ['organisation', orgId, 'tester-access'];

  const grants = useQuery<{ grants: TesterAccessGrantView[] }>({
    queryKey: key,
    queryFn: () => apiGet(`/api/v1/organisations/${orgId}/tester-access`),
    refetchInterval: (q) =>
      q.state.data?.grants.some((g) => g.status === 'queued' || g.status === 'dispatched')
        ? 15_000
        : false,
  });

  const send = useMutation({
    mutationFn: (testerEmail: string) =>
      apiSend('POST', `/api/v1/organisations/${orgId}/tester-access`, { testerEmail }),
    onSuccess: () => {
      toast.success('Sent to the hospital system. It will confirm here within a few minutes.');
      setEmail('');
      void qc.invalidateQueries({ queryKey: key });
    },
    onError: (e) =>
      toast.error(e instanceof ApiError ? e.message : 'Could not request tester access.'),
  });

  const retry = useMutation({
    mutationFn: () => apiSend('POST', `/api/v1/organisations/${orgId}/test-hospital/retry`),
    onSuccess: () => {
      toast.success('Sent again. It carries on from where it stopped.');
      void qc.invalidateQueries({ queryKey: key });
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Could not retry.'),
  });

  const building = grants.data?.grants.some(
    (g) => g.kind === 'build' && (g.status === 'queued' || g.status === 'dispatched'),
  );
  const valid = /^[^\s@]+@sensesys\.org$/i.test(email.trim());

  return (
    <Card data-testid="tester-access-card">
      <CardHeader
        title="Tester access"
        subtitle="Give a SenseSys colleague working logins for every role — each one checked before they are emailed"
      />
      <CardBody className="space-y-4">
        <form
          className="flex flex-wrap items-end gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (valid) send.mutate(email.trim());
          }}
        >
          <div className="min-w-[18rem] flex-1">
            <Input
              label="Colleague's email"
              type="email"
              placeholder="name@sensesys.org"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              {...(email && !valid ? { error: 'Must be a @sensesys.org address' } : {})}
            />
          </div>
          <Button type="submit" disabled={!valid || building} loading={send.isPending}>
            Give tester access
          </Button>
        </form>

        {grants.isLoading ? (
          <LoadingState />
        ) : grants.isError ? (
          <ErrorState
            description="Previous requests could not be loaded."
            onRetry={() => void grants.refetch()}
          />
        ) : (grants.data?.grants.length ?? 0) === 0 ? (
          <p className="text-sm text-text-muted">
            No tester access has been given from Control yet.
          </p>
        ) : (
          <ul className="divide-y divide-border" data-testid="tester-access-grants">
            {grants.data!.grants.map((g, i) => (
              <GrantRow
                key={g.commandId}
                grant={g}
                retrying={retry.isPending}
                // Only the latest attempt, and only a failed build.
                {...(i === 0 && g.kind === 'build' && g.status === 'failed'
                  ? { onRetry: () => retry.mutate() }
                  : {})}
              />
            ))}
          </ul>
        )}
      </CardBody>
    </Card>
  );
}

function GrantRow({
  grant: g,
  onRetry,
  retrying,
}: {
  grant: TesterAccessGrantView;
  onRetry?: () => void;
  retrying: boolean;
}) {
  return (
    <li className="flex flex-wrap items-start justify-between gap-2 py-2 text-sm">
      <div>
        <p className="font-medium">
          {g.kind === 'build' ? 'Hospital built for ' : ''}
          {g.testerEmail}
        </p>
        <p className="text-xs text-text-muted" title={formatDateTime(g.requestedAt)}>
          requested {relativeTime(g.requestedAt)}
        </p>
        {g.status === 'failed' && g.error && <p className="mt-1 text-xs text-error">{g.error}</p>}
      </div>
      <div className="flex items-center gap-2">
        {onRetry && (
          <Button size="sm" variant="secondary" loading={retrying} onClick={onRetry}>
            Try again
          </Button>
        )}
        <GrantOutcome grant={g} />
      </div>
    </li>
  );
}

function GrantOutcome({ grant: g }: { grant: TesterAccessGrantView }) {
  if (g.status === 'queued' || g.status === 'dispatched') {
    return (
      <Badge tone="info">
        {g.kind === 'build' ? 'Building the hospital…' : 'Waiting for the hospital system…'}
      </Badge>
    );
  }
  if (g.status === 'acknowledged' && g.seatsTotal !== null && g.seatsVerified !== null) {
    const allGood = g.seatsVerified === g.seatsTotal && g.emailed === true;
    return (
      <Badge tone={allGood ? 'success' : 'warning'}>
        {g.seatsVerified} of {g.seatsTotal} logins working
        {g.emailed ? ' · emailed' : ' · not emailed'}
      </Badge>
    );
  }
  if (g.status === 'acknowledged') {
    // Acknowledged as a redelivery: the hospital system had already done it.
    return <Badge tone="neutral">Already done earlier</Badge>;
  }
  if (g.status === 'failed') return <Badge tone="error">Not done</Badge>;
  return <Badge tone="warning">{g.status.replace('_', ' ')}</Badge>;
}
